import React, {useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {
    Box,
    Container,
    Stack,
    Text,
    IconButton,
    Link as ChakraLink,
    useBreakpointValue
} from '@chakra-ui/react'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {CloseIcon} from '../../../components/custom-icons'

// Constants
import {HEADER_BANNER_STORAGE_NAME} from '../../../constants'

// Utils
import {getSessionJSONItem, setSessionJSONItem} from '../../../utils/utils'
import {useUrlNotLocalizedFromLink} from '../../helpers/urlHelper'
import Link from '@salesforce/retail-react-app/app/components/link'

/**
 * PDCHeaderTopBanner is a responsive React component that displays a dismissible top banner in the page header,
 * supporting dynamic messages and links based on the current viewport size. The banner can display different
 * messages and links for small, medium, and large breakpoints, and supports both internal and external links.
 * The visibility state of the banner is persisted in session storage to ensure a consistent user experience
 * across page reloads. The component also allows for custom link overrides via the `custom` prop.
 *
 * Props:
 * @param {string} smallMessage - The message to display on small screens.
 * @param {string} mediumMessage - The message to display on medium screens.
 * @param {string} largeMessage - The message to display on large screens.
 * @param {object} smallLink - The link object for small screens.
 * @param {object} mediumLink - The link object for medium screens.
 * @param {object} largeLink - The link object for large screens.
 * @param {object} custom - Optional custom link overrides for each breakpoint.
 *
 * The component uses Chakra UI for styling and layout, and integrates with internationalization and routing
 * utilities. The banner message can include a <link>...</link> tag, which will be replaced with the appropriate
 * link element. The banner can be closed by the user, and this state is remembered for the session.
 */
export const PDCHeaderTopBanner = ({
    smallMessage,
    mediumMessage,
    largeMessage,
    smallLink,
    mediumLink,
    largeLink,
    custom
}) => {
    const intl = useIntl()
    const message = useBreakpointValue({
        base: smallMessage,
        md: mediumMessage,
        lg: largeMessage
    })
    const link = useBreakpointValue({
        base: custom?.smallLink || smallLink,
        md: custom?.mediumLink || mediumLink,
        lg: custom?.largeLink || largeLink
    })
    const updatedUrl = useUrlNotLocalizedFromLink(link) || link
    const isLinkExternal = link?.type === 'EXTERNAL'

    const [showBanner, setShowBanner] = useState(false)

    useEffect(() => {
        const isBannerVisible = getSessionJSONItem(HEADER_BANNER_STORAGE_NAME) ?? true
        setShowBanner(isHydrated() && isBannerVisible)
    }, [isHydrated()])

    /**
     * Updates the visibility state of the header top banner and persists this state in session storage.
     *
     * @function setBannerVisibility
     * @param {boolean} isVisible - A boolean value indicating whether the banner should be visible.
     *
     * This function performs two main actions:
     * 1. It stores the visibility state of the banner in the session storage using a predefined key.
     * 2. It updates the local state to reflect the current visibility of the banner.
     *
     * The session storage is used to maintain the visibility state across page reloads, ensuring a consistent user experience.
     */
    const setBannerVisibility = (isVisible) => {
        setSessionJSONItem(HEADER_BANNER_STORAGE_NAME, isVisible)
        setShowBanner(isVisible)
    }

    const renderMessageWithLink = (message) => {
        const linkTagRegex = /<link>(.*?)<\/link>/
        const parts = message.split(linkTagRegex)

        return (
            <span>
                {parts.map((part, index) => {
                    if (index % 2 === 1 && updatedUrl) {
                        return isLinkExternal ? (
                            <ChakraLink key={index} href={updatedUrl} isExternal color="white">
                                <Text as="span" variant="descriptor">
                                    {part}
                                </Text>
                            </ChakraLink>
                        ) : (
                            <Link key={index} to={updatedUrl} color="white">
                                <Text as="span" variant="descriptor">
                                    {part}
                                </Text>
                            </Link>
                        )
                    }
                    return part.length > 0 ? (
                        <span key={index} dangerouslySetInnerHTML={{__html: part}} />
                    ) : (
                        part
                    )
                })}
            </span>
        )
    }

    return (
        showBanner &&
        message && (
            <Box as="section" maxWidth="container.xxxl" mx="auto" id="headerBanner">
                <Box color="white" bg="blue" position={'relative'}>
                    <Container
                        py={['10px', null, '6px']}
                        pl={['10px', null, '16px', null, '40px']}
                        pr={['32px', null, '34px', null, '58px']}
                    >
                        <Stack direction="row" justify="center" align={'center'}>
                            <Text variant="descriptor">{renderMessageWithLink(message)}</Text>
                            <IconButton
                                position={'absolute'}
                                right={['0', null, '10px', null, '34px']}
                                padding={['10px', null, '6px']}
                                size="auto"
                                icon={<CloseIcon boxSize={3} />}
                                aria-label={intl.formatMessage({
                                    defaultMessage: 'Close banner',
                                    id: 'header.top_banner.close'
                                })}
                                onClick={() => setBannerVisibility(false)}
                            />
                        </Stack>
                    </Container>
                </Box>
            </Box>
        )
    )
}

PDCHeaderTopBanner.propTypes = {
    smallMessage: PropTypes.string,
    mediumMessage: PropTypes.string,
    largeMessage: PropTypes.string,
    smallLink: PropTypes.string,
    mediumLink: PropTypes.string,
    largeLink: PropTypes.string,
    custom: PropTypes.shape({
        smallLink: PropTypes.shape({
            target: PropTypes.string,
            type: PropTypes.string
        }),
        mediumLink: PropTypes.shape({
            target: PropTypes.string,
            type: PropTypes.string
        }),
        largeLink: PropTypes.shape({
            target: PropTypes.string,
            type: PropTypes.string
        })
    })
}
